import type { DashboardCellOf, DashboardQueryColumn, DashboardQueryColumnType, DashboardQueryResult } from './DashboardData';

/** Every query key the server implements. A layout naming anything else names nothing.*/
export const DASHBOARD_QUERY_KEYS = [
    'exampleCount',
    'participantCount',
    'participantsByCountry',
    'participantsBySex',
    'ageDistribution',
    'ageAtDiagnosis',
    'topFirstSymptoms',
    'formMissingness',
] as const;

export type DashboardQueryKey = (typeof DASHBOARD_QUERY_KEYS)[number];

/** What an author sees in a bound widget while editing */
export type DashboardQueryFixtures = { [K in DashboardQueryKey]: DashboardQueryResult };

/** Narrows a key read out of a stored layout blob, which is untrusted JSON. */
export function isDashboardQueryKey(value: unknown): value is DashboardQueryKey {
    return typeof value === 'string' && DASHBOARD_QUERY_KEYS.some((key): boolean => key === value);
}

/**
 * One input a query takes, declared once. The server builds this parameter's validation schema
 * from it, and the settings sidebar draws this parameter's input from it, so the two cannot drift.
 */
export type DashboardQueryParam = {
    /** i18n key */
    label: string;
    /** Which primitive the author enters.*/
    type: DashboardQueryColumnType;
    /** Present means the query runs without it. */
    optional?: true;
    /** When present the author picks one of these instead of typing. */
    choices?: readonly [string, ...string[]];
};

/** What Composer needs to bind a chart to a query without running it first. */
export type DashboardQueryDescriptor = {
    /** i18n key */
    label: string;
    description?: string;
    /**
     * The columns the query projects. A query may narrow its projection for a caller who cannot see
     * every row, so this is the widest shape the query returns, not a guarantee for one caller.
     */
    columns: readonly DashboardQueryColumn[];
    /** The inputs the query takes, in the order an author should fill them. */
    params?: Readonly<Record<string, DashboardQueryParam>>;
};

/** Every registered query, described once. */
export const DASHBOARD_QUERY_CATALOG = {
    exampleCount: {
        label: 'Dashboard.Query.ExampleCount',
        columns: [{ key: 'patientCount', label: 'Patients', type: 'number' }],
    },
    participantCount: {
        label: 'Dashboard.Query.ParticipantCount.Label',
        columns: [{ key: 'participantCount', label: 'Dashboard.Query.ParticipantCount.Columns.Count', type: 'number' }],
        params: { excludeSelf: { label: 'Dashboard.Query.ParticipantCount.Params.ExcludeSelf', type: 'boolean', optional: true } },
    },
    participantsByCountry: {
        label: 'Dashboard.Query.ParticipantsByCountry.Label',
        columns: [
            { key: 'country', label: 'Dashboard.Query.ParticipantsByCountry.Columns.Country', type: 'string' },
            { key: 'count', label: 'Dashboard.Query.ParticipantsByCountry.Columns.Count', type: 'number' },
        ],
    },
    participantsBySex: {
        label: 'Dashboard.Query.ParticipantsBySex.Label',
        columns: [
            { key: 'category', label: 'Dashboard.Query.ParticipantsBySex.Columns.Category', type: 'string' },
            { key: 'count', label: 'Dashboard.Query.ParticipantsBySex.Columns.Count', type: 'number' },
        ],
    },
    ageDistribution: {
        label: 'Dashboard.Query.AgeDistribution.Label',
        columns: [
            { key: 'category', label: 'Dashboard.Query.AgeDistribution.Columns.Category', type: 'string' },
            { key: 'count', label: 'Dashboard.Query.AgeDistribution.Columns.Count', type: 'number' },
        ],
    },
    ageAtDiagnosis: {
        label: 'Dashboard.Query.AgeAtDiagnosis.Label',
        columns: [
            { key: 'category', label: 'Dashboard.Query.AgeAtDiagnosis.Columns.Category', type: 'string' },
            { key: 'count', label: 'Dashboard.Query.AgeAtDiagnosis.Columns.Count', type: 'number' },
        ],
        params: { questionKey: { label: 'Dashboard.Query.AgeAtDiagnosis.Params.QuestionKey', type: 'string' } },
    },
    topFirstSymptoms: {
        label: 'Dashboard.Query.TopFirstSymptoms.Label',
        columns: [
            { key: 'category', label: 'Dashboard.Query.TopFirstSymptoms.Columns.Category', type: 'string' },
            { key: 'count', label: 'Dashboard.Query.TopFirstSymptoms.Columns.Count', type: 'number' },
        ],
        params: {
            questionKey: { label: 'Dashboard.Query.TopFirstSymptoms.Params.QuestionKey', type: 'string' },
            limit: { label: 'Dashboard.Query.TopFirstSymptoms.Params.Limit', type: 'number', optional: true },
        },
    },
    formMissingness: {
        label: 'Dashboard.Query.FormMissingness.Label',
        columns: [
            { key: 'patientId', label: 'Dashboard.Query.FormMissingness.Columns.Patient', type: 'string' },
            { key: 'siteName', label: 'Dashboard.Query.FormMissingness.Columns.Site', type: 'string' },
            { key: 'formName', label: 'Dashboard.Query.FormMissingness.Columns.Form', type: 'string' },
            { key: 'missingQuestions', label: 'Dashboard.Query.FormMissingness.Columns.MissingQuestions', type: 'number', sumWhenGrouped: true },
            { key: 'totalQuestions', label: 'Dashboard.Query.FormMissingness.Columns.TotalQuestions', type: 'number', sumWhenGrouped: true },
            // { key: 'submissionsWithMissing', label: 'Dashboard.Query.FormMissingness.Columns.SubmissionsWithMissing', type: 'number' },
            // { key: 'totalSubmissions', label: 'Dashboard.Query.FormMissingness.Columns.TotalSubmissions', type: 'number' },
            { key: 'openQueries', label: 'Dashboard.Query.FormMissingness.Columns.OpenQueries', type: 'number', sumWhenGrouped: true },
            { key: 'totalQueries', label: 'Dashboard.Query.FormMissingness.Columns.TotalQueries', type: 'number', sumWhenGrouped: true },
        ],
    },
} as const satisfies Record<DashboardQueryKey, DashboardQueryDescriptor>;

/** One query's declared columns, correlated to its key. */
export type DashboardQueryColumnsOf<TKey extends DashboardQueryKey> = (typeof DASHBOARD_QUERY_CATALOG)[TKey]['columns'];

/** The parameters a query declares, as plain data. */
export function dashboardQueryParams(queryKey: DashboardQueryKey): Readonly<Record<string, DashboardQueryParam>> {
    const descriptor: DashboardQueryDescriptor = DASHBOARD_QUERY_CATALOG[queryKey];
    return descriptor.params ?? {};
}

/** One query's declared parameters, correlated to its key. */
type DashboardQueryParamsSpecOf<TKey extends DashboardQueryKey> = (typeof DASHBOARD_QUERY_CATALOG)[TKey] extends {
    readonly params: infer TParams;
}
    ? TParams
    : Record<PropertyKey, never>;

/** What one declared parameter is worth once parsed: a fixed choice when it lists any, its primitive otherwise. */
type DashboardQueryParamValue<TParam> = TParam extends { readonly choices: readonly (infer TChoice)[] }
    ? TChoice
    : TParam extends { readonly type: infer TType extends DashboardQueryColumnType }
      ? DashboardCellOf<TType>
      : never;

/** The names a query lets the author leave blank. */
type OptionalParamNames<TSpec> = {
    [TName in keyof TSpec]: TSpec[TName] extends { readonly optional: true } ? TName : never;
}[keyof TSpec];

/** Every declared parameter's parsed value, before the optional ones are made optional. */
type ParamValues<TSpec> = { [TName in keyof TSpec]: DashboardQueryParamValue<TSpec[TName]> };

/** The parsed values, with the parameters the author may leave blank marked optional. */
type ParsedParams<TSpec> = Omit<ParamValues<TSpec>, OptionalParamNames<TSpec>> &
    Partial<Pick<ParamValues<TSpec>, Extract<OptionalParamNames<TSpec>, keyof TSpec>>>;

/** What one query's body receives once its parameters are parsed. */
export type DashboardQueryParamsOf<TKey extends DashboardQueryKey> = ParsedParams<DashboardQueryParamsSpecOf<TKey>>;
